import {
  AuthErrorCodes,
  type AuthProvider,
  FacebookAuthProvider,
  GoogleAuthProvider,
  signInWithPopup,
} from 'firebase/auth';

import { handleAuthError } from '@auth/AuthErrorHandler';
import { FACEBOOK_LOGIN_PERMISSIONS, GOOGLE_SIGN_IN_SCOPES } from '@auth/FederatedScopes';
import { logInfo } from '@helpers/Loggers';
import { auth } from '@helpers/firebase/Initialize';

type FederatedAuthRequest = {
  prompt: () => Promise<unknown>;
  disabled: boolean;
};

const FEDERATED_POPUP_CANCELED_CODES = new Set<string>([
  AuthErrorCodes.EXPIRED_POPUP_REQUEST,
  AuthErrorCodes.POPUP_CLOSED_BY_USER,
  AuthErrorCodes.USER_CANCELLED,
]);

export function useGoogleAuthRequest(): FederatedAuthRequest {
  return { prompt: signInWithGoogle, disabled: false };
}

export function useFacebookAuthRequest(): FederatedAuthRequest {
  return { prompt: signInWithFacebook, disabled: false };
}

async function signInWithGoogle() {
  await signInWithPopupProvider(getGoogleAuthProvider(), 'Google');
}

function getGoogleAuthProvider() {
  const provider = new GoogleAuthProvider();
  GOOGLE_SIGN_IN_SCOPES.forEach((scope) => {
    provider.addScope(scope);
  });
  return provider;
}

async function signInWithFacebook() {
  await signInWithPopupProvider(getFacebookAuthProvider(), 'Facebook');
}

function getFacebookAuthProvider() {
  const provider = new FacebookAuthProvider();
  FACEBOOK_LOGIN_PERMISSIONS.forEach((permission) => {
    provider.addScope(permission);
  });
  return provider;
}

async function signInWithPopupProvider(provider: AuthProvider, displayName: string) {
  try {
    await signInWithPopup(auth, provider);
  } catch (error) {
    if (isFederatedPopupCanceled(error)) {
      logInfo(`${displayName} sign in canceled by user`);
      return;
    }

    handleAuthError(error);
  }
}

function isFederatedPopupCanceled(error: unknown) {
  if (!hasErrorCode(error)) {
    return false;
  }
  return FEDERATED_POPUP_CANCELED_CODES.has(error.code);
}

function hasErrorCode(error: unknown): error is { code: string } {
  return (
    typeof error === 'object' && error !== null && 'code' in error && typeof error.code === 'string'
  );
}

export function useAppleAuthRequest(): FederatedAuthRequest {
  return { prompt: signInWithApple, disabled: true };
}

export async function signInWithApple() {
  logInfo('Apple sign in is not available on web.');
}
